import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const POLL_INTERVAL = 30000; // 30 seconds

const TYPE_ICONS = {
  new_message:           '💬',
  new_request:           '📋',
  request_accepted:      '✅',
  request_declined:      '❌',
  proposal_received:     '📩',
  proposal_accepted:     '✅',
  job_cancelled:         '🚫',
  dispute_raised:        '⚠️',
  checkin:               '📍',
  completion_confirmed:  '✅',
  payment_authorized:    '💳',
  payout_sent:           '💰',
  rating_received:       '⭐',
  verification_update:   '🔒',
  promo:                 '📢',
};

const resolveName = (obj, lang) =>
  obj?.[lang] ?? obj?.['es'] ?? obj?.['en'] ?? (typeof obj === 'string' ? obj : '');

const timeAgo = (iso, t, lang) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('notification.just_now');
  if (mins < 60) return t('notification.time_min', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('notification.time_hour', { count: hrs });
  return new Date(iso).toLocaleDateString(lang, { day: '2-digit', month: '2-digit' });
};

const entityPath = (type, id) => {
  if (!id) return '/dashboard';
  if (type === 'service_request' || type === 'message') return `/service/${id}`;
  if (type === 'user') return `/providers/${id}`;
  return '/dashboard';
};

const NotificationBell = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [unread, setUnread] = useState(null); // null = not yet loaded
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const panelRef = useRef(null);
  const bellRef = useRef(null);

  // ── Fetch unread count ─────────────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnread(res.data.count);
    } catch {
      // non-fatal — keep existing count
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchCount]);

  // ── Fetch full list on open ────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch {
      // non-fatal
    } finally {
      setLoadingList(false);
    }
  }, []);

  const toggleOpen = () => {
    if (!open) fetchList();
    setOpen(o => !o);
  };

  // ── Close on outside click / Escape ───────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const escHandler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, []);

  // ── Mark individual notification read ─────────────────────────────────────
  const markRead = async (notif) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n)
    );
    setUnread(prev => Math.max(0, (prev ?? 0) - (notif.is_read ? 0 : 1)));

    try {
      await axios.post(`${API}/notifications/mark-read`, {
        notification_ids: [notif.id],
      });
    } catch {
      // revert optimistic on failure
      setNotifications(prev =>
        prev.map(n => n.id === notif.id ? { ...n, is_read: notif.is_read } : n)
      );
    }

    setOpen(false);
    navigate(entityPath(notif.entity_type, notif.entity_id));
  };

  // ── Mark all read ──────────────────────────────────────────────────────────
  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (!unreadIds.length) return;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread(0);

    try {
      await axios.post(`${API}/notifications/mark-read`, {
        notification_ids: unreadIds,
      });
    } catch {
      // revert
      fetchList();
      fetchCount();
    }
  };

  // ── Badge label ────────────────────────────────────────────────────────────
  const badgeLabel = unread == null ? null : unread === 0 ? null : unread > 9 ? '9+' : String(unread);

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={bellRef}
        onClick={toggleOpen}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label={t('notification.title')}
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {badgeLabel && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
            {badgeLabel}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-10 w-80 bg-white rounded-xl border shadow-xl z-50 flex flex-col"
          style={{ maxHeight: '480px' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
            <span className="font-semibold text-sm">{t('notification.title')}</span>
            {notifications.some(n => !n.is_read) && (
              <button
                onClick={markAllRead}
                className="text-xs text-primary hover:underline"
              >
                {t('notification.mark_all_read')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {loadingList ? (
              <div className="py-8 text-center text-sm text-muted-foreground">…</div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {t('notification.empty')}
              </div>
            ) : (
              notifications.map(notif => {
                const icon = TYPE_ICONS[notif.type] || '🔔';
                const title = resolveName(notif.title, i18n.language);
                const body = resolveName(notif.body, i18n.language);
                const ago = timeAgo(notif.created_at, t, i18n.language);
                const isUnread = !notif.is_read;

                return (
                  <button
                    key={notif.id}
                    onClick={() => markRead(notif)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex gap-3 items-start border-b last:border-b-0 ${
                      isUnread ? 'border-l-2 border-l-primary bg-blue-50/40' : ''
                    }`}
                  >
                    <span className="text-lg flex-shrink-0 mt-0.5">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {body.length > 80 ? body.slice(0, 80) + '…' : body}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">{ago}</p>
                    </div>
                    {isUnread && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer — only if list is full (30 items) */}
          {notifications.length >= 30 && (
            <div className="border-t px-4 py-2 flex-shrink-0">
              {/* TODO: /notifications route not yet implemented */}
              <button
                onClick={() => { setOpen(false); navigate('/dashboard'); }}
                className="text-xs text-primary hover:underline"
              >
                {t('notification.view_all')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
